/**
 * Widget 消息区域组件
 * 纯消息列表渲染，不包含输入框
 *
 * 设计规范（来自 pencil）：
 * - 背景：#F8F9FA
 * - padding：16px
 * - 消息间距：12px
 *
 * 可访问性：
 * - aria-label 标识消息区域
 * - role="log" 用于消息列表
 * - aria-live="polite" 用于实时消息更新
 */

import React, { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import { useChatStore } from '../../stores/chatStore';
import { clsx } from 'clsx';

export interface WidgetMessageAreaProps {
  /** 是否显示建议卡片 */
  showSuggestions?: boolean;
  /** 建议卡片点击回调 */
  onSuggestionClick?: (label: string) => void;
  /** 自定义 CSS 类名 */
  className?: string;
}

// 建议卡片数据 - Widget 模式使用横向滚动布局
const suggestions = [
  {
    id: 'product',
    category: '产品',
    label: '产品咨询推荐',
    icon: (
      <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    color: 'var(--accent-orange)',
    bg: '#fff7ed',
  },
  {
    id: 'order',
    category: '订单',
    label: '查询订单状态',
    icon: (
      <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    color: 'var(--accent-green)',
    bg: '#ecfdf5',
  },
  {
    id: 'refund',
    category: '售后',
    label: '申请退货退款',
    icon: (
      <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    color: 'var(--accent-purple)',
    bg: '#f5f3ff',
  },
  {
    id: 'service',
    category: '客服',
    label: '联系人工客服',
    icon: (
      <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    color: 'var(--accent-blue)',
    bg: '#f0f9ff',
  },
];

export const WidgetMessageArea: React.FC<WidgetMessageAreaProps> = ({
  showSuggestions = true,
  onSuggestionClick,
  className,
}) => {
  const { messages, isTyping, sendMessage, connect, disconnect } = useChatStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [messages]);

  // WebSocket 连接管理
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  const handleSuggestionClick = (label: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(label);
    } else {
      sendMessage(label);
    }
  };

  return (
    <div
      className={clsx(
        'flex-1 overflow-y-auto',
        'bg-[#F8F9FA]',
        'p-[16px]',
        className
      )}
      aria-label="聊天消息区域"
    >
      {messages.length === 0 && showSuggestions ? (
        // 空状态：问候语 + 建议卡片
        <div className="flex flex-col items-center justify-center h-full">
          {/* 问候语 */}
          <div className="text-center mb-[24px]">
            <p className="text-[14px] leading-[20px] text-[var(--text-muted)]">
              您好！我是AI智能客服
            </p>
          </div>

          {/* 建议卡片 - 横向滚动布局 */}
          <div className="flex gap-[8px] w-full overflow-x-auto pb-[8px]" role="list" aria-label="快捷提问">
            {suggestions.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSuggestionClick(item.label)}
                className="flex-shrink-0 flex items-center gap-[8px] px-[12px] py-[8px] rounded-lg transition-all duration-200 cursor-pointer focus-visible:outline-none hover:scale-105"
                style={{ backgroundColor: item.bg }}
                aria-label={`快捷提问: ${item.label}`}
              >
                <span className="flex-shrink-0" style={{ color: item.color }}>
                  {item.icon}
                </span>
                <span className="text-[12px] leading-[16px] font-medium text-[var(--text-primary)] whitespace-nowrap">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* 消息列表 */}
          <div
            className="flex flex-col gap-[12px]"
            role="log"
            aria-live="polite"
            aria-label="对话消息"
          >
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                {...message}
                onActionClick={handleSuggestionClick}
              />
            ))}
          </div>

          {/* AI 打字中指示器 */}
          {isTyping && (
            <div className="flex mb-4" role="status" aria-label="AI 正在输入">
              <div className="px-[16px] py-[12px] bg-white rounded-2xl rounded-bl-sm text-[14px] leading-[20px] text-[var(--bubble-ai-text)] shadow-sm">
                <div className="flex space-x-1">
                  <span
                    className="w-2 h-2 bg-[var(--accent-primary)] rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-[var(--accent-primary)] rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-[var(--accent-primary)] rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 滚动锚点 */}
      <div ref={messagesEndRef} />
    </div>
  );
};
